import { useEffect, useState } from 'react';
import api, { BACKEND } from '@/lib/api';
import { Twitter, Download, Trophy, Users, Eye } from 'lucide-react';

export default function WeeklyRoundupCard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    api.get('/sponsorships/roundup').then(r => {
      setData(r.data);
      setState(r.data.sponsorships?.length ? 'ready' : 'empty');
    }).catch(() => setState('empty'));
  }, []);

  if (state === 'loading' || state === 'empty' || !data) return null;

  const base = BACKEND;
  const pngUrl = `${base}/api/sponsorships/roundup.png?week_start=${encodeURIComponent(data.week_start)}`;
  const tweet = `${data.totals.count} sponsors · ${data.totals.revenue_usdt} USDT · ${data.totals.votes.toLocaleString()} votes · ${data.totals.clicks.toLocaleString()} clicks 🚀\n\nThis week on @BITGONODE Coin Battles →`;
  const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`;

  return (
    <div className="mt-10 border border-[#B26BFF]/30 bg-gradient-to-br from-[#B26BFF]/8 via-[#181A20] to-[#181A20] p-5" data-testid="weekly-roundup-card">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-[#B26BFF]" />
        <h2 className="text-white text-xl font-semibold">Weekly Sponsor Roundup</h2>
        <span className="text-xs uppercase tracking-wider text-[#848E9C]">week of {data.week_start?.slice(0, 10)}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-xs uppercase text-[#848E9C]">Sponsors</div>
          <div className="font-mono text-white text-2xl mt-1">{data.totals.count}</div>
        </div>
        <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/8 p-3">
          <div className="text-xs uppercase text-[#0ECB81]">Revenue</div>
          <div className="font-mono text-[#0ECB81] text-2xl mt-1">{data.totals.revenue_usdt.toLocaleString()}</div>
          <div className="text-[10px] text-[#5E6673]">USDT</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-xs uppercase text-[#848E9C] flex items-center gap-1"><Users className="w-3 h-3" /> Votes</div>
          <div className="font-mono text-[#FCD535] text-2xl mt-1">{data.totals.votes.toLocaleString()}</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-xs uppercase text-[#848E9C] flex items-center gap-1"><Eye className="w-3 h-3" /> Clicks</div>
          <div className="font-mono text-[#F6465D] text-2xl mt-1">{data.totals.clicks.toLocaleString()}</div>
        </div>
      </div>
      {data.sponsorships.length > 0 && (
        <div className="mt-4 border border-[#2B3139] bg-[#0B0E11]" data-testid="roundup-list">
          <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
            <div className="col-span-2">Coin</div>
            <div className="col-span-4">Sponsor</div>
            <div className="col-span-2 text-right">Bid</div>
            <div className="col-span-2 text-right">Votes</div>
            <div className="col-span-2 text-right">Clicks</div>
          </div>
          {data.sponsorships.slice(0, 8).map(r => (
            <div key={r.sponsorship_id} className="grid grid-cols-12 px-4 py-2 items-center border-b border-[#2B3139]/60 text-sm"
              data-testid={`roundup-row-${r.sponsorship_id}`}>
              <div className="col-span-2 flex items-center gap-1.5">
                {r.coin_image && <img src={r.coin_image} alt="" className="w-5 h-5" />}
                <span className="font-mono text-white text-xs">{r.coin_symbol}</span>
              </div>
              <div className="col-span-4 text-white text-xs truncate flex items-center gap-1">
                {r.sponsor_name}
                {r.verified && <span className="text-[#0ECB81] text-[10px]">✓</span>}
              </div>
              <div className="col-span-2 text-right font-mono text-[#FCD535] text-xs">{Math.round(r.price_usdt || 0)}</div>
              <div className="col-span-2 text-right font-mono text-[#B7BDC6] text-xs">{r.votes_for + r.votes_against}</div>
              <div className="col-span-2 text-right font-mono text-[#B7BDC6] text-xs">{r.clicks}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <a href={intentUrl} target="_blank" rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#1DA1F2]/40 bg-[#1DA1F2]/10 text-[#1DA1F2] text-sm font-semibold rounded-sm hover:bg-[#1DA1F2]/20"
          data-testid="share-roundup-btn">
          <Twitter className="w-4 h-4" /> Share roundup on X
        </a>
        <a href={pngUrl} download={`cryptox_roundup_${data.week_start?.slice(0, 10)}.png`}
          className="flex-1 flex items-center justify-center gap-2 py-2 border border-[#2B3139] bg-[#0B0E11] text-[#B7BDC6] text-sm font-semibold rounded-sm hover:text-white hover:border-[#B26BFF]/50"
          data-testid="download-roundup-png">
          <Download className="w-4 h-4" /> Download PNG banner
        </a>
      </div>
    </div>
  );
}
